import { Fragment, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { adminHeaders, api } from "../../lib/api";
import { rewardStatusLabel, rewardTypeLabel } from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";
import { money, shortDate } from "../../lib/format";

interface LinkedReward {
  reward_id: number;
  reward_type: string;
  reward_label?: string | null;
  amount: number;
  status: string | null;
  task_id: number | null;
  task_status: string | null;
  dispatch_mode: string | null;
}

interface DepositCandidateTrace {
  id: number;
  status: string | null;
  source: string | null;
  pending_reason?: string | null;
  note?: string | null;
  evidence_note?: string | null;
  channel?: string | null;
  confirmed_deposit_id?: number | null;
}

interface DepositTaskUsageTrace {
  activation_deposit: boolean;
  step3_deposit: boolean;
  step5_deposit: boolean;
  is_used_by_newbie_task: boolean;
  used_roles: string[];
  label: string;
}

interface DepositRewardTrace {
  has_reward: boolean;
  reward_count: number;
  label: string;
}

interface DepositStep6Trace {
  has_step6_voucher: boolean;
  has_red_packet_draw: boolean;
  member_has_step6_voucher: boolean;
  member_has_red_packet_draw: boolean;
  voucher_count: number;
  legacy_draw_count: number;
  v2_draw_count: number;
  label: string;
}

interface DepositDispatchTrace {
  has_dispatch_task: boolean;
  task_count: number;
  label: string;
}

interface DepositRow {
  id: number;
  member_account: string;
  amount: number;
  deposited_at: string;
  note?: string | null;
  is_test: number;
  created_at: string;
  source?: string | null;
  source_type?: string | null;
  external_id?: string | null;
  candidate_deposit_id?: number | null;
  currency?: string | null;
  status?: string | null;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  linked_rewards?: LinkedReward[];
  candidate_trace?: DepositCandidateTrace | null;
  task_usage_trace?: DepositTaskUsageTrace;
  reward_trace?: DepositRewardTrace;
  step6_trace?: DepositStep6Trace;
  dispatch_trace?: DepositDispatchTrace;
}

interface CandidateDepositRow {
  id: number;
  member_account: string;
  member_id: number | null;
  amount: number;
  currency: string | null;
  deposited_at: string;
  source: string | null;
  external_id: string | null;
  status: string;
  raw_json: string | null;
  created_at: string | null;
  updated_at: string | null;
  confirmed_deposit_id: number | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  reject_reason: string | null;
  ignored_at: string | null;
  ignored_by: string | null;
  ignore_reason: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  member_exists: boolean;
  duplicate_warning: DuplicateWarning | null;
  linked_deposit_summary: LinkedDepositSummary | null;
  can_manual_confirm?: boolean;
  manual_confirm_disabled_reason?: string | null;
  next_step_message?: string | null;
}

interface DuplicateWarning {
  type: string;
  message: string;
  deposit_id?: number;
}

interface LinkedDepositSummary {
  id: number;
  member_account: string | null;
  amount: number | null;
  deposited_at: string | null;
  source: string | null;
  external_id: string | null;
  candidate_deposit_id: number | null;
  status: string | null;
}

interface CandidateListResponse {
  rows: CandidateDepositRow[];
  total: number;
  limit: number;
  offset: number;
}

interface CandidateActionResult {
  candidate?: {
    id: number;
    status: string;
    confirmed_deposit_id?: number | null;
    reviewed_at?: string | null;
    reviewed_by?: string | null;
  };
  deposit?: {
    id: number;
    member_account: string;
    amount: number;
    deposited_at: string;
  };
  regular_rebate_auto?: Record<string, unknown>;
}

interface ManualCandidateResult {
  status: "created" | "duplicate";
  duplicate: boolean;
  reason: string;
  candidate: {
    id: number;
    external_id: string | null;
    member_account: string;
    amount: number;
    currency: string | null;
    deposited_at: string;
    status: string;
    confirmed_deposit_id: number | null;
    raw_json?: string | null;
  } | null;
}

interface HighsenSyncStatus {
  lastRunAt: string | null;
  mode: string;
  sender: string;
  adGroup: string | null;
  status: string | null;
  dateMode: string;
  dateFrom: string | null;
  dateTo: string | null;
  actualDateFrom: string | null;
  actualDateTo: string | null;
  lookbackMinutes: number | null;
  overlapMinutes: number | null;
  totalRows: number;
  validCount: number;
  skippedCount: number;
  errorCount: number;
  totalDepositAmount: number;
  importedCount: number;
  duplicateCount: number;
  pendingReviewCount: number;
  autoConfirmedCount: number;
  importedExternalIds: string[];
  duplicateExternalIds: string[];
  stoppedReason: string | null;
  lockStatus: string;
  sessionStatus: "ok" | "missing" | "expired" | "unknown";
  needsManualLogin: boolean;
  safetySummary: string[];
  sourceCode: string;
  cursorValue: string | null;
  formalSenderLocked: boolean;
  schedulerMode: string | null;
  schedulerRunIndex: number | null;
  schedulerMaxRuns: number | null;
  schedulerIntervalSeconds: number | null;
  schedulerStartedAt: string | null;
  schedulerRunStartedAt: string | null;
  schedulerNextRunAt: string | null;
  schedulerLastRunStatus: string | null;
  schedulerTotalRuns: number;
  schedulerSuccessfulRuns: number;
  schedulerFailedRuns: number;
  schedulerDuplicateRuns: number;
}

type ActiveTab = "candidates" | "deposits";
type CandidateActionType = "confirm" | "reject" | "ignore";
type JsonObject = Record<string, unknown>;

interface HighsenCandidateInfo {
  sourceLabel: string;
  giftId: string;
  sender: string;
  receiver: string;
  giftAmount: number | null;
  rate: number | null;
  adGroup: string;
  rawStatus: string;
  importStatus: string;
  pendingReason: string;
}

interface ManualCandidateInfo {
  sourceLabel: string;
  memberAccount: string;
  amount: number | null;
  depositedAt: string;
  channel: string;
  externalId: string;
  note: string;
  evidenceNote: string;
  importStatus: string;
  pendingReason: string;
}

type CandidateFilters = {
  status: string;
  memberAccount: string;
  externalId: string;
  dateFrom: string;
  dateTo: string;
  minAmount: string;
  maxAmount: string;
  source: string;
};

const candidateStatusLabels: Record<string, string> = {
  pending: "待人工處理",
  confirmed: "已轉正式",
  rejected: "已拒絕",
  ignored: "已忽略",
  duplicate: "重複資料"
};

const candidateStatusClasses: Record<string, string> = {
  pending: "warn",
  confirmed: "success",
  rejected: "danger",
  ignored: "muted",
  duplicate: "danger"
};

export function AdminDepositsPage() {
  const isLocalDev = isLocalDevFrontend();
  const [activeTab, setActiveTab] = useState<ActiveTab>("candidates");

  const [deposits, setDeposits] = useState<DepositRow[]>([]);
  const [depositMemberAccount, setDepositMemberAccount] = useState("");
  const [depositDateFrom, setDepositDateFrom] = useState("");
  const [depositDateTo, setDepositDateTo] = useState("");
  const [depositMinAmount, setDepositMinAmount] = useState("");
  const [depositMaxAmount, setDepositMaxAmount] = useState("");
  const [depositTestFilter, setDepositTestFilter] = useState("");
  const [depositError, setDepositError] = useState("");

  const [candidateRows, setCandidateRows] = useState<CandidateDepositRow[]>([]);
  const [candidateStatus, setCandidateStatus] = useState("pending");
  const [candidateMemberAccount, setCandidateMemberAccount] = useState("");
  const [candidateExternalId, setCandidateExternalId] = useState("");
  const [candidateDateFrom, setCandidateDateFrom] = useState("");
  const [candidateDateTo, setCandidateDateTo] = useState("");
  const [candidateMinAmount, setCandidateMinAmount] = useState("");
  const [candidateMaxAmount, setCandidateMaxAmount] = useState("");
  const [candidateSource, setCandidateSource] = useState("");
  const [candidateError, setCandidateError] = useState("");
  const [candidateTotal, setCandidateTotal] = useState(0);

  const [expandedCandidateIds, setExpandedCandidateIds] = useState<number[]>([]);
  const [actionDraft, setActionDraft] = useState<{ type: CandidateActionType; candidate: CandidateDepositRow } | null>(null);
  const [actionReason, setActionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionResult, setActionResult] = useState("");
  const [manualMemberAccount, setManualMemberAccount] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [manualDepositedAt, setManualDepositedAt] = useState("");
  const [manualChannel, setManualChannel] = useState("龍來喬");
  const [manualExternalId, setManualExternalId] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualEvidenceNote, setManualEvidenceNote] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualResult, setManualResult] = useState("");
  const [highsenSyncStatus, setHighsenSyncStatus] = useState<HighsenSyncStatus | null>(null);
  const [highsenSyncError, setHighsenSyncError] = useState("");

  async function loadDeposits() {
    setDepositError("");
    try {
      setDeposits(await api<DepositRow[]>("/api/admin/deposits", { headers: adminHeaders() }));
    } catch (err) {
      setDeposits([]);
      setDepositError(toMessage(err, "正式充值紀錄讀取失敗，請稍後再試。"));
    }
  }

  async function loadHighsenSyncStatus() {
    setHighsenSyncError("");
    try {
      setHighsenSyncStatus(await api<HighsenSyncStatus>("/api/admin/highsen-sync/status", { headers: adminHeaders() }));
    } catch (err) {
      setHighsenSyncStatus(null);
      setHighsenSyncError(toMessage(err, "Highsen 同步狀態讀取失敗。"));
    }
  }

  function currentCandidateFilters(): CandidateFilters {
    return {
      status: candidateStatus,
      memberAccount: candidateMemberAccount,
      externalId: candidateExternalId,
      dateFrom: candidateDateFrom,
      dateTo: candidateDateTo,
      minAmount: candidateMinAmount,
      maxAmount: candidateMaxAmount,
      source: candidateSource
    };
  }

  async function loadCandidates(filters: CandidateFilters = currentCandidateFilters()) {
    setCandidateError("");
    const params = new URLSearchParams();
    params.set("status", filters.status || "pending");
    if (filters.memberAccount.trim()) params.set("member_account", filters.memberAccount.trim());
    if (filters.externalId.trim()) params.set("external_id", filters.externalId.trim());
    if (filters.dateFrom) params.set("date_from", filters.dateFrom);
    if (filters.dateTo) params.set("date_to", filters.dateTo);
    if (filters.minAmount) params.set("min_amount", filters.minAmount);
    if (filters.maxAmount) params.set("max_amount", filters.maxAmount);
    if (filters.source.trim()) params.set("source", filters.source.trim());
    params.set("limit", "80");

    try {
      const response = await api<CandidateListResponse>(`/api/admin/candidate-deposits?${params.toString()}`, {
        headers: adminHeaders()
      });
      setCandidateRows(response.rows);
      setCandidateTotal(response.total);
    } catch (err) {
      setCandidateRows([]);
      setCandidateTotal(0);
      setCandidateError(toMessage(err, "候選充值讀取失敗，請稍後再試。"));
    }
  }

  useEffect(() => {
    loadCandidates();
    loadDeposits();
    loadHighsenSyncStatus();
  }, []);

  const filteredDeposits = useMemo(() => deposits.filter((row) => {
    const accountMatch = !depositMemberAccount.trim() || row.member_account.toLowerCase().includes(depositMemberAccount.trim().toLowerCase());
    const date = String(row.deposited_at ?? row.created_at ?? "").slice(0, 10);
    const fromMatch = !depositDateFrom || date >= depositDateFrom;
    const toMatch = !depositDateTo || date <= depositDateTo;
    const minMatch = !depositMinAmount || Number(row.amount) >= Number(depositMinAmount);
    const maxMatch = !depositMaxAmount || Number(row.amount) <= Number(depositMaxAmount);
    const testMatch = !depositTestFilter || String(row.is_test) === depositTestFilter;
    return accountMatch && fromMatch && toMatch && minMatch && maxMatch && testMatch;
  }), [deposits, depositMemberAccount, depositDateFrom, depositDateTo, depositMinAmount, depositMaxAmount, depositTestFilter]);

  function resetCandidateFilters() {
    const filters: CandidateFilters = {
      status: "pending",
      memberAccount: "",
      externalId: "",
      dateFrom: "",
      dateTo: "",
      minAmount: "",
      maxAmount: "",
      source: ""
    };
    setCandidateStatus("pending");
    setCandidateMemberAccount("");
    setCandidateExternalId("");
    setCandidateDateFrom("");
    setCandidateDateTo("");
    setCandidateMinAmount("");
    setCandidateMaxAmount("");
    setCandidateSource("");
    void loadCandidates(filters);
  }

  function resetDepositFilters() {
    setDepositMemberAccount("");
    setDepositDateFrom("");
    setDepositDateTo("");
    setDepositMinAmount("");
    setDepositMaxAmount("");
    setDepositTestFilter("");
  }

  function toggleCandidateDetails(candidateId: number) {
    setExpandedCandidateIds((current) => (
      current.includes(candidateId)
        ? current.filter((id) => id !== candidateId)
        : [...current, candidateId]
    ));
  }

  function openCandidateAction(type: CandidateActionType, candidate: CandidateDepositRow) {
    setActionDraft({ type, candidate });
    setActionReason("");
    setActionError("");
    setActionResult("");
  }

  async function runCandidateAction() {
    if (!actionDraft) return;
    if (actionDraft.type !== "confirm" && !actionReason.trim()) {
      setActionError("請先填寫原因。");
      return;
    }

    setActionLoading(true);
    setActionError("");
    try {
      const path = `/api/admin/candidate-deposits/${actionDraft.candidate.id}/${actionDraft.type}`;
      const body = actionDraft.type === "confirm"
        ? { confirmed_by: "admin", admin_note: "confirmed from /admin/deposits" }
        : { reviewed_by: "admin", reason: actionReason.trim() };
      const result = await api<CandidateActionResult>(path, {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify(body)
      });
      setActionResult(formatCandidateActionResult(actionDraft.type, result));
      setActionDraft(null);
      await Promise.all([loadCandidates(), loadDeposits()]);
    } catch (err) {
      setActionError(toMessage(err, "候選充值操作失敗，請稍後再試。"));
    } finally {
      setActionLoading(false);
    }
  }

  async function createManualCandidate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setManualError("");
    setManualResult("");
    const memberAccount = manualMemberAccount.trim();
    const note = manualNote.trim();
    if (!memberAccount) {
      setManualError("請填寫會員帳號。");
      return;
    }
    if (!manualAmount || Number(manualAmount) <= 0) {
      setManualError("請填寫大於 0 的充值金額。");
      return;
    }
    if (!manualDepositedAt) {
      setManualError("請填寫充值時間。");
      return;
    }
    if (!note) {
      setManualError("請填寫備註。");
      return;
    }

    setManualLoading(true);
    try {
      const result = await api<ManualCandidateResult>("/api/admin/candidate-deposits/manual", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          memberAccount,
          amount: Number(manualAmount),
          currency: "TWD",
          depositedAt: manualDepositedAt,
          externalId: manualExternalId.trim() || undefined,
          channel: manualChannel.trim() || undefined,
          note,
          evidenceNote: manualEvidenceNote.trim() || undefined,
          source: "manual_admin"
        })
      });
      const externalId = result.candidate?.external_id ?? manualExternalId.trim() ?? "-";
      if (result.duplicate) {
        setManualResult(`外部單號已存在，未新增第二筆：${externalId}`);
      } else {
        setManualResult(`建立成功：${externalId}，狀態 pending。仍需人工確認，不會自動推任務。`);
        setManualMemberAccount("");
        setManualAmount("");
        setManualDepositedAt("");
        setManualExternalId("");
        setManualNote("");
        setManualEvidenceNote("");
      }
      setCandidateStatus("pending");
      setCandidateExternalId(externalId === "-" ? "" : externalId);
      await loadCandidates({
        status: "pending",
        memberAccount: "",
        externalId: externalId === "-" ? "" : externalId,
        dateFrom: "",
        dateTo: "",
        minAmount: "",
        maxAmount: "",
        source: ""
      });
    } catch (err) {
      setManualError(toMessage(err, "手動新增候選充值失敗，請稍後再試。"));
    } finally {
      setManualLoading(false);
    }
  }

  return (
    <AdminLayout title="充值資料" subtitle="安全充值會由系統自動確認；只有異常充值才會留在待人工處理池。">
      <section className="admin-card">
        <div className="admin-card-head">
          <span>DEPOSIT REVIEW</span>
          <h2>充值資料中心</h2>
        </div>
        <div className="tabs deposit-tabs" role="tablist" aria-label="充值資料頁籤">
          <button className={activeTab === "candidates" ? "active" : ""} type="button" onClick={() => setActiveTab("candidates")}>
            待人工處理
          </button>
          <button className={activeTab === "deposits" ? "active" : ""} type="button" onClick={() => setActiveTab("deposits")}>
            正式充值紀錄
          </button>
        </div>
      </section>

      <HighsenSyncStatusPanel status={highsenSyncStatus} error={highsenSyncError} />

      {actionResult ? <p className="note">{actionResult}</p> : null}

      {activeTab === "candidates" ? (
        <section className="admin-card deposit-tab-panel">
          <div className="admin-card-head">
            <span>CANDIDATES</span>
            <h2>異常充值待人工處理</h2>
          </div>
          <form className="manual-candidate-form" onSubmit={createManualCandidate}>
            <div className="manual-candidate-head">
              <div>
                <span className="section-label">MANUAL BACKUP</span>
                <h3>手動新增候選充值</h3>
              </div>
              <p>爬蟲異常或漏抓時使用。新增後只會建立「候選充值」，不會直接入帳、不會推任務、不會開紅包。</p>
            </div>
            <div className="manual-candidate-grid">
              <label>
                會員帳號
                <input value={manualMemberAccount} onChange={(event) => setManualMemberAccount(event.target.value)} placeholder="例如 cbi5007" />
              </label>
              <label>
                充值金額
                <input type="number" min="1" value={manualAmount} onChange={(event) => setManualAmount(event.target.value)} placeholder="1000" />
              </label>
              <label>
                充值時間
                <input type="datetime-local" value={manualDepositedAt} onChange={(event) => setManualDepositedAt(event.target.value)} />
              </label>
              <label>
                渠道 / 廣告群組
                <input value={manualChannel} onChange={(event) => setManualChannel(event.target.value)} placeholder="龍來喬" />
              </label>
              <label>
                外部單號 / 備註單號（可選）
                <input value={manualExternalId} onChange={(event) => setManualExternalId(event.target.value)} placeholder="manual-cbi5007-20260719-001" />
              </label>
              <label>
                備註
                <input value={manualNote} onChange={(event) => setManualNote(event.target.value)} placeholder="爬蟲異常，Dolly 人工補候選充值" />
              </label>
              <label className="manual-candidate-wide">
                證據備註（可選）
                <textarea value={manualEvidenceNote} onChange={(event) => setManualEvidenceNote(event.target.value)} placeholder="例如：Highsen 後台已確認玩家充值" />
              </label>
            </div>
            <div className="manual-candidate-actions">
              <button className="admin-btn admin-btn-primary" type="submit" disabled={manualLoading}>
                {manualLoading ? "建立中..." : "新增候選充值"}
              </button>
              <span>只會建立 candidate_deposits pending；仍需人工確認，不會自動推 Step5 / Step6 / 紅包 / 派點。</span>
            </div>
            {manualError ? <p className="error-box">{manualError}</p> : null}
            {manualResult ? <p className="note">{manualResult}</p> : null}
          </form>
          <AdminFilterBar>
            <label>
              狀態
              <select value={candidateStatus} onChange={(event) => setCandidateStatus(event.target.value)}>
                <option value="pending">待人工處理</option>
                <option value="confirmed">已轉正式</option>
                <option value="rejected">已拒絕</option>
                <option value="ignored">已忽略</option>
                <option value="duplicate">重複資料</option>
                <option value="all">全部狀態</option>
              </select>
            </label>
            <label>
              會員帳號
              <input value={candidateMemberAccount} onChange={(event) => setCandidateMemberAccount(event.target.value)} placeholder={isLocalDev ? "test050" : "輸入會員帳號"} />
            </label>
            <label>
              外部交易 ID
              <input value={candidateExternalId} onChange={(event) => setCandidateExternalId(event.target.value)} placeholder={isLocalDev ? "dep_test_058" : "輸入外部交易 ID"} />
            </label>
            <label>
              充值起始日
              <input type="date" value={candidateDateFrom} onChange={(event) => setCandidateDateFrom(event.target.value)} />
            </label>
            <label>
              充值結束日
              <input type="date" value={candidateDateTo} onChange={(event) => setCandidateDateTo(event.target.value)} />
            </label>
            <label>
              最低金額
              <input type="number" min="0" value={candidateMinAmount} onChange={(event) => setCandidateMinAmount(event.target.value)} />
            </label>
            <label>
              最高金額
              <input type="number" min="0" value={candidateMaxAmount} onChange={(event) => setCandidateMaxAmount(event.target.value)} />
            </label>
            <label>
              來源
              <input value={candidateSource} onChange={(event) => setCandidateSource(event.target.value)} placeholder="crawler" />
            </label>
            <button className="admin-btn admin-btn-primary" type="button" onClick={() => loadCandidates()}>查詢</button>
            <button className="admin-btn admin-btn-secondary" type="button" onClick={resetCandidateFilters}>重設</button>
          </AdminFilterBar>

          {candidateError ? <p className="error-box">{candidateError}</p> : null}

          <div className="summary-list">
            <p>目前顯示 {candidateRows.length} 筆，符合條件共 {candidateTotal} 筆。正常安全充值會自動建立正式 deposits；這裡只處理未通過自動確認的異常資料。</p>
          </div>

          <div className="candidate-card-list">
            {candidateRows.length ? candidateRows.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                expanded={expandedCandidateIds.includes(candidate.id)}
                onToggle={() => toggleCandidateDetails(candidate.id)}
                onAction={openCandidateAction}
              />
            )) : <p className="empty-state">目前沒有符合條件的異常充值資料。</p>}
          </div>
        </section>
      ) : (
        <section className="admin-card deposit-tab-panel">
          <div className="admin-card-head">
            <span>DEPOSITS</span>
            <h2>正式充值紀錄</h2>
          </div>
          <AdminFilterBar>
            <label>
              會員帳號
              <input value={depositMemberAccount} onChange={(event) => setDepositMemberAccount(event.target.value)} placeholder={isLocalDev ? "test003" : "輸入會員帳號"} />
            </label>
            <label>
              起始日期
              <input type="date" value={depositDateFrom} onChange={(event) => setDepositDateFrom(event.target.value)} />
            </label>
            <label>
              結束日期
              <input type="date" value={depositDateTo} onChange={(event) => setDepositDateTo(event.target.value)} />
            </label>
            <label>
              最低金額
              <input type="number" min="0" value={depositMinAmount} onChange={(event) => setDepositMinAmount(event.target.value)} />
            </label>
            <label>
              最高金額
              <input type="number" min="0" value={depositMaxAmount} onChange={(event) => setDepositMaxAmount(event.target.value)} />
            </label>
            <label>
              測試資料
              <select value={depositTestFilter} onChange={(event) => setDepositTestFilter(event.target.value)}>
                <option value="">全部</option>
                <option value="1">local/dev 測試</option>
                <option value="0">正式紀錄</option>
              </select>
            </label>
            <button className="admin-btn admin-btn-primary" type="button" onClick={loadDeposits}>查詢</button>
            <button className="admin-btn admin-btn-secondary" type="button" onClick={resetDepositFilters}>重設</button>
          </AdminFilterBar>

          {depositError ? <p className="error-box">{depositError}</p> : null}

          <div className="summary-list">
            <p>目前顯示 {filteredDeposits.length} 筆正式充值。系統自動確認或人工確認後，都會在這裡看到 source、external_id 與 candidate_deposit_id。</p>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>充值 ID</th>
                  <th>會員帳號</th>
                  <th>金額</th>
                  <th>充值時間</th>
                  <th>來源</th>
                  <th>外部交易 ID</th>
                  <th>候選 ID</th>
                  <th>關聯獎勵</th>
                  <th>狀態</th>
                  <th>確認時間</th>
                  <th>確認人</th>
                  <th>備註</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeposits.map((row) => (
                  <Fragment key={row.id}>
                    <tr>
                      <td>#{row.id}</td>
                      <td>{display(row.member_account)}</td>
                      <td>{money(row.amount)}</td>
                      <td>{formatDate(row.deposited_at)}</td>
                      <td>{depositSourceLabel(row)}</td>
                      <td>{display(row.external_id)}</td>
                      <td>{row.candidate_deposit_id ? `#${row.candidate_deposit_id}` : "-"}</td>
                      <td><DepositLinkedRewards deposit={row} /></td>
                      <td>{display(depositStatusLabel(row.status))}</td>
                      <td>{formatDate(row.confirmed_at)}</td>
                      <td>{display(row.confirmed_by)}</td>
                      <td>{display(row.note)}</td>
                    </tr>
                    {hasDepositTrace(row) ? (
                      <tr className="deposit-trace-row">
                        <td colSpan={12}>
                          <DepositTracePanel deposit={row} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {actionDraft ? (
        <div className="game-modal-backdrop">
          <section className={`game-modal confirm-dialog ${actionDraft.type === "confirm" ? "info" : "error"}`}>
            <div className="modal-orb" />
            <h2>{candidateActionTitle(actionDraft.type)}</h2>
            <div className="modal-body">
              <p>{candidateActionMessage(actionDraft.type, actionDraft.candidate)}</p>
              {actionDraft.type !== "confirm" ? (
                <label className="candidate-reason-field">
                  處理原因
                  <textarea value={actionReason} onChange={(event) => setActionReason(event.target.value)} placeholder="請輸入客服處理原因" />
                </label>
              ) : null}
              {actionError ? <p className="error-box">{actionError}</p> : null}
            </div>
            <div className="modal-actions">
              <button className="secondary-cta" type="button" disabled={actionLoading} onClick={() => setActionDraft(null)}>
                取消
              </button>
              <button className="primary-cta" type="button" disabled={actionLoading || (actionDraft.type !== "confirm" && !actionReason.trim())} onClick={runCandidateAction}>
                {actionLoading ? "處理中..." : candidateActionConfirmText(actionDraft.type)}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </AdminLayout>
  );
}

function HighsenSyncStatusPanel({ status, error }: { status: HighsenSyncStatus | null; error: string }) {
  const operationalClass = status ? highsenOperationalStatusClass(status) : "muted";
  const autoConfirmClass = status?.autoConfirmedCount === 0 ? "success" : "danger";
  return (
    <section className="admin-card highsen-sync-card" aria-label="Highsen 同步狀態">
      <div className="admin-card-head">
        <span>HIGHSEN SYNC</span>
        <h2>Highsen 同步狀態</h2>
      </div>

      {error ? <p className="error-box">{error}</p> : null}
      {!status && !error ? <p className="empty-state">尚未讀到 Highsen 同步狀態。</p> : null}

      {status ? (
        <>
          <div className="highsen-sync-operational-head">
            <span className={`status-pill ${operationalClass}`}>同步狀態：{highsenOperationalStatusLabel(status)}</span>
            <span className="status-pill success">測試 sender：test888</span>
            <span className="status-pill locked">正式 sender：lxw8119 尚未啟用</span>
            <span className={`status-pill ${autoConfirmClass}`}>自動確認：{status.autoConfirmedCount}</span>
          </div>

          {status.needsManualLogin ? (
            <p className="highsen-sync-alert">Highsen 登入已過期，請重新執行 npm run highsen:login:manual。</p>
          ) : null}

          <div className="highsen-sync-summary-grid">
            <HighsenKpi label="最近同步時間" value={formatDate(status.lastRunAt)} />
            <HighsenKpi label="抓到筆數" value={status.totalRows} />
            <HighsenKpi label="新增候選充值" value={status.importedCount} />
            <HighsenKpi label="重複略過" value={status.duplicateCount} />
            <HighsenKpi label="錯誤筆數" value={status.errorCount} tone={status.errorCount > 0 ? "warn" : "normal"} />
            <HighsenKpi label="自動確認" value={status.autoConfirmedCount} tone={status.autoConfirmedCount > 0 ? "danger" : "normal"} />
          </div>

          <p className="highsen-sync-note">
            安全提示：只進 candidate，不自動入帳、不推任務、不開紅包、不派點。
          </p>

          <details className="highsen-sync-technical">
            <summary>展開技術資料</summary>
            <div className="highsen-sync-status-row">
              <span className={`status-pill ${status.sessionStatus === "ok" ? "success" : status.needsManualLogin ? "danger" : "muted"}`}>Session：{highsenSessionStatusLabel(status)}</span>
              <span className={`status-pill ${highsenLockStatusClass(status.lockStatus)}`}>Lock：{highsenLockStatusLabel(status.lockStatus)}</span>
              <span className="status-pill muted">模式：{display(status.mode)}</span>
              <span className="status-pill muted">Date mode：{highsenDateModeLabel(status.dateMode)}</span>
              {status.schedulerMode ? (
                <span className="status-pill live">Local watch：{highsenSchedulerModeLabel(status.schedulerMode)}</span>
              ) : null}
              <span className={`status-pill ${status.stoppedReason ? "warn" : "success"}`}>停止原因：{status.stoppedReason ?? "ok"}</span>
            </div>
            <div className="highsen-sync-grid">
              <InfoItem label="目前 sender" value={status.sender} />
              <InfoItem label="廣告群組" value={status.adGroup} />
              <InfoItem label="Highsen 狀態" value={status.status} />
              <InfoItem label="查詢起始" value={status.actualDateFrom ?? status.dateFrom} />
              <InfoItem label="查詢結束" value={status.actualDateTo ?? status.dateTo} />
              <InfoItem label="Lookback 分鐘" value={status.lookbackMinutes ?? "-"} />
              <InfoItem label="Overlap 分鐘" value={status.overlapMinutes ?? "-"} />
              <InfoItem label="Scheduler" value={status.schedulerMode ? highsenSchedulerModeLabel(status.schedulerMode) : "-"} />
              <InfoItem label="排程間隔秒數" value={status.schedulerIntervalSeconds ?? "-"} />
              <InfoItem label="Run 進度" value={schedulerRunLabel(status)} />
              <InfoItem label="最後 run 狀態" value={highsenSchedulerStatusLabel(status.schedulerLastRunStatus)} />
              <InfoItem label="下一次 run" value={formatDate(status.schedulerNextRunAt)} />
              <InfoItem label="總 run 數" value={status.schedulerTotalRuns || "-"} />
              <InfoItem label="成功 run" value={status.schedulerSuccessfulRuns || "-"} />
              <InfoItem label="失敗 run" value={status.schedulerFailedRuns || "-"} />
              <InfoItem label="Duplicate run" value={status.schedulerDuplicateRuns || "-"} />
              <InfoItem label="有效筆數" value={status.validCount} />
              <InfoItem label="跳過筆數" value={status.skippedCount} />
              <InfoItem label="候選待確認" value={status.pendingReviewCount} />
              <InfoItem label="換算充值總額" value={money(status.totalDepositAmount)} />
              <InfoItem label="Source code" value={status.sourceCode} />
              <InfoItem label="Cursor" value={status.cursorValue} />
            </div>
            <div className="highsen-sync-ids">
              <span>匯入：{status.importedExternalIds.length ? status.importedExternalIds.join(", ") : "無"}</span>
              <span>重複：{status.duplicateExternalIds.length ? status.duplicateExternalIds.join(", ") : "無"}</span>
              <span>Safety：{status.safetySummary.length ? status.safetySummary.join("、") : "只進 candidate"}</span>
            </div>
          </details>
        </>
      ) : null}
    </section>
  );
}

function HighsenKpi({ label, value, tone = "normal" }: { label: string; value: string | number | null | undefined; tone?: "normal" | "warn" | "danger" }) {
  return (
    <div className={`highsen-sync-kpi ${tone}`}>
      <span>{label}</span>
      <strong>{display(value)}</strong>
    </div>
  );
}

function DepositTracePanel({ deposit }: { deposit: DepositRow }) {
  const candidate = deposit.candidate_trace;
  const taskUsage = deposit.task_usage_trace;
  const rewardTrace = deposit.reward_trace;
  const step6Trace = deposit.step6_trace;
  const dispatchTrace = deposit.dispatch_trace;
  const linkedRewardCount = deposit.linked_rewards?.length ?? 0;

  return (
    <section className="deposit-trace-panel" aria-label={`充值 #${deposit.id} 追蹤狀態`}>
      <div className="deposit-trace-head">
        <div>
          <span className="section-label">正式充值追蹤</span>
          <strong>來源：{depositSourceLabel(deposit)}</strong>
        </div>
        <span className="status-pill success">目前狀態：{depositFormalStatusLabel(deposit.status)}</span>
      </div>

      <div className="deposit-trace-grid">
        <InfoItem label="會員帳號" value={deposit.member_account} />
        <InfoItem label="充值金額" value={money(deposit.amount)} />
        <InfoItem label="幣別" value={deposit.currency ?? "TWD"} />
        <InfoItem label="外部單號" value={deposit.external_id} />
        <InfoItem label="Candidate ID" value={candidate?.id ? `#${candidate.id}` : deposit.candidate_deposit_id ? `#${deposit.candidate_deposit_id}` : "-"} />
        <InfoItem label="候選狀態" value={candidate?.status ? candidateStatusLabel(candidate.status) : "-"} />
        <InfoItem label="渠道" value={candidate?.channel ?? "-"} />
        <InfoItem label="備註" value={candidate?.note ?? deposit.note ?? "-"} />
        <InfoItem label="證據備註" value={candidate?.evidence_note ?? "-"} />
        <InfoItem label="匯入原因" value={candidate?.pending_reason ?? "-"} />
      </div>

      <div className="deposit-trace-status-list">
        <span className={`status-pill ${taskUsage?.is_used_by_newbie_task ? "warn" : "muted"}`}>
          任務使用：{depositTaskUsageLabel(taskUsage)}
        </span>
        <span className={`status-pill ${rewardTrace?.has_reward ? "warn" : "muted"}`}>
          Reward：{rewardTrace?.label ?? "尚未建立 reward"}
        </span>
        <span className={`status-pill ${(step6Trace?.has_step6_voucher || step6Trace?.has_red_packet_draw) ? "warn" : "muted"}`}>
          Step6 紅包：{step6Trace?.label ?? "尚未建立 Step6 voucher / red packet"}
        </span>
        <span className={`status-pill ${dispatchTrace?.has_dispatch_task ? "warn" : "muted"}`}>
          派點任務：{dispatchTrace?.label ?? "尚未建立 dispatch task"}
        </span>
        <span className={`status-pill ${linkedRewardCount > 0 ? "warn" : "muted"}`}>
          linked_rewards：{linkedRewardCount > 0 ? `${linkedRewardCount} 筆` : "空"}
        </span>
      </div>

      <p className="deposit-trace-note">
        提醒：這筆充值目前只入正式充值紀錄，尚未推任務、尚未發獎、尚未開紅包。
      </p>
    </section>
  );
}

function hasDepositTrace(deposit: DepositRow) {
  return Boolean(
    deposit.candidate_trace ||
    deposit.task_usage_trace ||
    deposit.reward_trace ||
    deposit.step6_trace ||
    deposit.dispatch_trace ||
    deposit.linked_rewards?.length
  );
}

function depositSourceLabel(deposit: DepositRow) {
  const source = deposit.candidate_trace?.source ?? deposit.source ?? deposit.source_type;
  if (source === "manual_admin") return "後台手動補充值";
  if (source === "highsen_gift_list_test") return "Highsen 贈禮列表（test888）";
  if (source === "highsen_gift_list") return "Highsen 贈禮列表";
  if (source === "crawler") return "爬蟲匯入充值";
  if (source === "manual") return "手動建立充值";
  return display(source ?? (deposit.is_test ? "local-dev" : "-"));
}

function depositFormalStatusLabel(status: string | null | undefined) {
  if (status === "confirmed") return "已確認為正式充值";
  return depositStatusLabel(status);
}

function highsenSessionStatusLabel(status: HighsenSyncStatus) {
  if (status.sessionStatus === "ok") return "正常";
  if (status.sessionStatus === "expired") return "已過期，需要 Dolly 重新登入";
  if (status.sessionStatus === "missing") return "尚未建立 session";
  return "狀態待確認";
}

function highsenOperationalStatusLabel(status: HighsenSyncStatus) {
  if (status.sessionStatus === "expired" || status.needsManualLogin) return "session 過期";
  if (status.lockStatus === "sync_locked" || status.lockStatus === "running") return "鎖定中";
  if (status.stoppedReason) return "需處理";
  return "正常";
}

function highsenOperationalStatusClass(status: HighsenSyncStatus) {
  if (status.sessionStatus === "expired" || status.needsManualLogin) return "danger";
  if (status.lockStatus === "sync_locked" || status.lockStatus === "running" || status.stoppedReason) return "warn";
  return "success";
}

function highsenLockStatusLabel(status: string | null | undefined) {
  if (status === "running") return "執行中";
  if (status === "sync_locked") return "已有同步執行中";
  if (status === "stale_lock_replaced") return "已替換過期 lock";
  return "閒置";
}

function highsenLockStatusClass(status: string | null | undefined) {
  if (status === "sync_locked") return "danger";
  if (status === "stale_lock_replaced") return "warn";
  if (status === "running") return "warn";
  return "success";
}

function highsenDateModeLabel(mode: string | null | undefined) {
  if (mode === "rolling") return "rolling";
  return "explicit";
}

function highsenSchedulerModeLabel(mode: string | null | undefined) {
  if (mode === "local_watch") return "本機排程 dry-run";
  return display(mode);
}

function highsenSchedulerStatusLabel(status: string | null | undefined) {
  if (status === "ok") return "完成";
  if (status === "failed") return "失敗";
  if (status === "skipped") return "略過";
  return "-";
}

function schedulerRunLabel(status: HighsenSyncStatus) {
  if (!status.schedulerMode) return "-";
  const runIndex = status.schedulerRunIndex ?? status.schedulerTotalRuns ?? "-";
  const maxRuns = status.schedulerMaxRuns ?? "-";
  return `${runIndex} / ${maxRuns}`;
}

function depositTaskUsageLabel(trace?: DepositTaskUsageTrace) {
  if (!trace?.is_used_by_newbie_task) return "尚未被入盟 / Step3 / Step5 使用";
  const roles = [
    trace.activation_deposit ? "入盟啟動筆" : "",
    trace.step3_deposit ? "Step3 二存" : "",
    trace.step5_deposit ? "Step5 三存" : ""
  ].filter(Boolean);
  return roles.length ? `已被 ${roles.join("、")} 使用` : trace.label;
}

function DepositLinkedRewards({ deposit }: { deposit: DepositRow }) {
  const rewards = deposit.linked_rewards ?? [];
  if (!rewards.length) {
    const amount = Number(deposit.amount);
    const message = Number.isFinite(amount) && amount < 1000
      ? "金額未達 1,000，未建立常態回饋"
      : "尚未建立關聯獎勵";
    return <span className="deposit-reward-empty">{message}</span>;
  }

  return (
    <div className="deposit-reward-summary">
      {rewards.map((reward) => (
        <div className="deposit-reward-item" key={`${deposit.id}-${reward.reward_id}`}>
          <strong>{linkedRewardLabel(reward)} #{reward.reward_id}</strong>
          <span>獎勵金額：{money(reward.amount)}</span>
          <span>派發狀態：{rewardStatusLabel(reward.status)}</span>
          <span>
            贈禮小幫手：{reward.task_id
              ? `${dispatchTaskStatusLabel(reward.task_status)}｜task #${reward.task_id}${reward.dispatch_mode ? `｜${dispatchModeLabel(reward.dispatch_mode)}` : ""}`
              : "尚未排入"}
          </span>
        </div>
      ))}
    </div>
  );
}

function linkedRewardLabel(reward: LinkedReward) {
  const apiLabel = display(reward.reward_label);
  if (apiLabel !== "-" && apiLabel !== reward.reward_type) return apiLabel;
  return rewardTypeLabel(reward.reward_type);
}

function CandidateCard({
  candidate,
  expanded,
  onToggle,
  onAction
}: {
  candidate: CandidateDepositRow;
  expanded: boolean;
  onToggle: () => void;
  onAction: (type: CandidateActionType, candidate: CandidateDepositRow) => void;
}) {
  const statusLabel = candidateStatusLabel(candidate.status);
  const statusClass = candidateStatusClasses[candidate.status] ?? "muted";
  const confirmDisabledReason = getConfirmDisabledReason(candidate);
  const reviewDisabledReason = getReviewDisabledReason(candidate);
  const highsenInfo = highsenCandidateInfo(candidate);
  const manualInfo = manualCandidateInfo(candidate);

  return (
    <article className="candidate-card">
      <header className="candidate-card-header">
        <div>
          <span className="section-label">候選 #{candidate.id}</span>
          <h3>{display(candidate.member_account)}</h3>
          <p>{formatDate(candidate.deposited_at)} · {display(candidate.source ?? "crawler")}</p>
        </div>
        <strong>{money(candidate.amount)}</strong>
      </header>

      <div className="candidate-card-badges">
        <span className={`status-pill ${statusClass}`}>{statusLabel}</span>
        <span className={`status-pill ${candidate.member_exists ? "success" : "danger"}`}>{candidate.member_exists ? "會員存在" : "找不到會員"}</span>
        {candidate.duplicate_warning ? <span className="status-pill danger">疑似重複</span> : null}
      </div>

      <div className="candidate-card-grid">
        <InfoItem label="外部交易 ID" value={candidate.external_id} />
        <InfoItem label="來源" value={candidate.source ?? "crawler"} />
        <InfoItem label="候選狀態" value={statusLabel} />
        <InfoItem label="建立時間" value={formatDate(candidate.created_at)} />
      </div>

      {highsenInfo ? (
        <section className="candidate-highsen-panel" aria-label="Highsen 贈禮列表候選充值">
          <div className="candidate-highsen-head">
            <strong>Highsen 贈禮列表</strong>
            <span>{highsenInfo.sourceLabel}</span>
          </div>
          <div className="candidate-card-grid">
            <InfoItem label="來源" value={highsenInfo.sourceLabel} />
            <InfoItem label="Highsen 贈禮 ID" value={highsenInfo.giftId || candidate.external_id} />
            <InfoItem label="贈送者" value={highsenInfo.sender} />
            <InfoItem label="接收者" value={highsenInfo.receiver || candidate.member_account} />
            <InfoItem label="Highsen 贈送金額" value={formatHighsenNumber(highsenInfo.giftAmount)} />
            <InfoItem label="換算充值金額" value={money(candidate.amount)} />
            <InfoItem label="換算比值" value={formatHighsenNumber(highsenInfo.rate)} />
            <InfoItem label="廣告群組" value={highsenInfo.adGroup} />
            <InfoItem label="Highsen 狀態" value={highsenInfo.rawStatus} />
            <InfoItem label="匯入狀態" value={highsenInfo.importStatus} />
            <InfoItem label="待確認原因" value={highsenInfo.pendingReason} />
          </div>
          {!candidate.member_exists ? (
            <p className="candidate-highsen-alert">會員帳號不存在，需先確認會員是否已綁定 / 匯入會員資料。這不是爬蟲錯誤。</p>
          ) : null}
        </section>
      ) : null}

      {manualInfo ? (
        <section className="candidate-manual-panel" aria-label="後台手動新增候選充值">
          <div className="candidate-highsen-head">
            <strong>後台手動新增</strong>
            <span>{manualInfo.sourceLabel}</span>
          </div>
          <div className="candidate-card-grid">
            <InfoItem label="來源" value={manualInfo.sourceLabel} />
            <InfoItem label="會員帳號" value={manualInfo.memberAccount} />
            <InfoItem label="充值金額" value={manualInfo.amount == null ? money(candidate.amount) : money(manualInfo.amount)} />
            <InfoItem label="充值時間" value={formatDate(manualInfo.depositedAt || candidate.deposited_at)} />
            <InfoItem label="渠道 / 廣告群組" value={manualInfo.channel} />
            <InfoItem label="外部單號" value={manualInfo.externalId || candidate.external_id} />
            <InfoItem label="備註" value={manualInfo.note} />
            <InfoItem label="證據備註" value={manualInfo.evidenceNote} />
            <InfoItem label="匯入狀態" value={manualInfo.importStatus} />
            <InfoItem label="Pending reason" value={manualInfo.pendingReason} />
          </div>
          {!candidate.member_exists ? (
            <p className="candidate-highsen-alert">會員帳號不存在，暫不可確認。請先確認會員是否已綁定 / 匯入會員資料。</p>
          ) : null}
        </section>
      ) : null}

      {candidate.duplicate_warning ? <p className="candidate-warning">{candidate.duplicate_warning.message}</p> : null}
      <p className="candidate-next-step">{candidateNextStep(candidate)}</p>

      <div className="candidate-action-row">
        <button className="admin-btn admin-btn-primary" type="button" disabled={Boolean(confirmDisabledReason)} title={confirmDisabledReason} onClick={() => onAction("confirm", candidate)}>
          人工確認為正式充值
        </button>
        <button className="admin-btn admin-btn-danger" type="button" disabled={Boolean(reviewDisabledReason)} title={reviewDisabledReason} onClick={() => onAction("reject", candidate)}>
          拒絕
        </button>
        <button className="admin-btn admin-btn-secondary" type="button" disabled={Boolean(reviewDisabledReason)} title={reviewDisabledReason} onClick={() => onAction("ignore", candidate)}>
          忽略
        </button>
        <a className="admin-btn admin-btn-secondary" href={`/admin/members/${encodeURIComponent(candidate.member_account)}`}>
          查看會員
        </a>
        <button className="admin-btn admin-btn-action" type="button" onClick={onToggle}>
          {expanded ? "收合明細" : "展開明細"}
        </button>
      </div>

      {confirmDisabledReason || reviewDisabledReason ? (
        <p className="candidate-disabled-note">{confirmDisabledReason || reviewDisabledReason}</p>
      ) : null}

      {expanded ? <CandidateDetails candidate={candidate} /> : null}
    </article>
  );
}

function CandidateDetails({ candidate }: { candidate: CandidateDepositRow }) {
  return (
    <div className="candidate-details">
      <div className="candidate-detail-grid">
        <InfoItem label="候選 ID" value={`#${candidate.id}`} />
        <InfoItem label="會員 ID" value={candidate.member_id ? `#${candidate.member_id}` : "-"} />
        <InfoItem label="幣別" value={candidate.currency ?? "coin"} />
        <InfoItem label="更新時間" value={formatDate(candidate.updated_at)} />
        <InfoItem label="正式充值 ID" value={candidate.confirmed_deposit_id ? `#${candidate.confirmed_deposit_id}` : "-"} />
        <InfoItem label="確認時間" value={formatDate(candidate.confirmed_at)} />
        <InfoItem label="確認人" value={candidate.confirmed_by} />
        <InfoItem label="審核時間" value={formatDate(candidate.reviewed_at)} />
        <InfoItem label="審核人" value={candidate.reviewed_by} />
        <InfoItem label="拒絕時間" value={formatDate(candidate.rejected_at)} />
        <InfoItem label="拒絕人" value={candidate.rejected_by} />
        <InfoItem label="拒絕原因" value={candidate.reject_reason} />
        <InfoItem label="忽略時間" value={formatDate(candidate.ignored_at)} />
        <InfoItem label="忽略人" value={candidate.ignored_by} />
        <InfoItem label="忽略原因" value={candidate.ignore_reason} />
      </div>

      {candidate.linked_deposit_summary ? (
        <div className="candidate-linked-deposit">
          <strong>關聯正式充值</strong>
          <p>
            #{candidate.linked_deposit_summary.id} · {display(candidate.linked_deposit_summary.member_account)} · {money(candidate.linked_deposit_summary.amount ?? 0)}
          </p>
          <p>來源 {display(candidate.linked_deposit_summary.source)} / 外部交易 {display(candidate.linked_deposit_summary.external_id)}</p>
        </div>
      ) : null}

      <details className="raw-details candidate-raw">
        <summary>查看原始資料</summary>
        <p>原始資料僅供工程查錯使用，客服日常可忽略。</p>
        <pre>{formatRawJson(candidate.raw_json)}</pre>
      </details>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="candidate-info-item">
      <span>{label}</span>
      <strong>{display(value)}</strong>
    </div>
  );
}

function candidateStatusLabel(status: string) {
  return candidateStatusLabels[status] ?? "待確認";
}

function depositStatusLabel(status: string | null | undefined) {
  if (!status) return "-";
  if (status === "confirmed") return "已確認";
  if (status === "pending") return "待確認";
  if (status === "rejected") return "已拒絕";
  if (status === "ignored") return "已忽略";
  return status;
}

function dispatchTaskStatusLabel(status?: string | null) {
  const labels: Record<string, string> = {
    pending: "等待贈禮小幫手",
    processing: "贈禮小幫手處理中",
    completed: "已派發完成",
    failed: "派發失敗",
    canceled: "已取消"
  };
  return status ? labels[status] ?? "狀態待確認" : "尚未排入";
}

function dispatchModeLabel(mode?: string | null) {
  const labels: Record<string, string> = {
    immediate_crawler_dispatch: "立即排入",
    next_day_12_crawler_dispatch: "明日 12 點後排入",
    manual_confirm_then_crawler_dispatch: "人工確認後排入"
  };
  return mode ? labels[mode] ?? "派發模式待確認" : "";
}

function getConfirmDisabledReason(candidate: CandidateDepositRow) {
  if (candidate.can_manual_confirm === false) {
    return candidate.manual_confirm_disabled_reason || "此候選充值目前不能直接轉正式充值。";
  }
  if (candidate.status !== "pending") return `${candidateStatusLabel(candidate.status)}，不可重複確認。`;
  if (!candidate.member_exists) return "找不到會員，不能確認為正式充值。";
  if (!display(candidate.external_id) || display(candidate.external_id) === "-") return "缺少外部交易 ID，不能直接轉正式充值。";
  if (!Number.isFinite(Number(candidate.amount)) || Number(candidate.amount) <= 0) return "充值金額異常，不能直接轉正式充值。";
  if (hasInvalidDateValue(candidate.deposited_at)) return "充值時間異常，不能直接轉正式充值。";
  if (candidate.duplicate_warning) return "已有相同來源交易的正式充值，請改用拒絕或忽略處理。";
  return "";
}

function getReviewDisabledReason(candidate: CandidateDepositRow) {
  if (candidate.status !== "pending") return `${candidateStatusLabel(candidate.status)}，不可重複處理。`;
  return "";
}

function candidateNextStep(candidate: CandidateDepositRow) {
  if (hasInvalidDateValue(candidate.deposited_at)) return "充值時間異常，請人工確認原始資料。";
  if (candidate.next_step_message) return candidate.next_step_message;
  if (candidate.status === "confirmed") return "已建立正式充值，不需要再操作。";
  if (candidate.status === "rejected") return "此候選充值已拒絕，不會建立正式充值。";
  if (candidate.status === "ignored") return "此候選充值已忽略，不會建立正式充值。";
  if (candidate.duplicate_warning) return "請先確認重複原因，避免同一外部交易建立兩筆正式充值。";
  if (!candidate.member_exists) return "會員帳號不存在，請先確認爬蟲資料或會員資料。";
  if (Number(candidate.amount) < 1000) return "此筆未通過自動確認；人工確認後只會建立正式充值，未滿 1,000 不會建立常態回饋 reward。";
  return "此筆未通過自動確認；請確認異常原因後，再決定人工確認、拒絕或忽略。";
}

function candidateActionTitle(type: CandidateActionType) {
  if (type === "confirm") return "確認建立正式充值？";
  if (type === "reject") return "確認拒絕候選充值？";
  return "確認忽略候選充值？";
}

function candidateActionMessage(type: CandidateActionType, candidate: CandidateDepositRow) {
  if (type === "confirm") {
    return `候選 #${candidate.id} 未由系統自動確認。人工確認後會建立正式 deposits；金額達常態回饋門檻時，會走既有 regular_deposit_rebate 自動建單流程。請確認不是重複外部交易後再繼續。`;
  }
  if (type === "reject") {
    return `拒絕候選 #${candidate.id} 後，不會建立正式充值，也不會建立 reward。請填寫拒絕原因。`;
  }
  return `忽略候選 #${candidate.id} 後，不會建立正式充值，也不會建立 reward。請填寫忽略原因。`;
}

function candidateActionConfirmText(type: CandidateActionType) {
  if (type === "confirm") return "確認建立";
  if (type === "reject") return "確認拒絕";
  return "確認忽略";
}

function formatCandidateActionResult(type: CandidateActionType, result: CandidateActionResult) {
  if (type === "reject") return `已拒絕候選充值 #${result.candidate?.id ?? "-"}。`;
  if (type === "ignore") return `已忽略候選充值 #${result.candidate?.id ?? "-"}。`;

  const depositId = result.deposit?.id ? `正式充值 #${result.deposit.id}` : "正式充值已建立";
  const rebate = result.regular_rebate_auto;
  const rewardId = nestedId(rebate, "reward") ?? numberField(rebate, "reward_id");
  const taskId = nestedId(rebate, "crawler_dispatch_auto") ?? nestedId(rebate, "crawler_task") ?? numberField(rebate, "task_id");
  const reason = stringField(rebate, "reason") ?? stringField(rebate, "skipped_reason");
  const parts = [`已確認候選充值 #${result.candidate?.id ?? "-"}，${depositId}`];
  if (rewardId) parts.push(`reward #${rewardId}`);
  if (taskId) parts.push(`贈禮小幫手任務 #${taskId}`);
  if (reason === "amount_below_minimum") parts.push("未滿 1,000，未建立常態回饋。");
  return `${parts.join(" / ")}。`;
}

function nestedId(input: Record<string, unknown> | undefined, key: string) {
  const value = input?.[key];
  if (!value || typeof value !== "object") return null;
  return numberField(value as Record<string, unknown>, "id") ?? numberField(value as Record<string, unknown>, "task_id");
}

function numberField(input: Record<string, unknown> | undefined, key: string) {
  const value = input?.[key];
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function stringField(input: Record<string, unknown> | undefined, key: string) {
  const value = input?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function formatDate(value: string | null | undefined) {
  if (!value) return "-";
  return hasInvalidDateValue(value) ? "日期格式異常" : shortDate(value);
}

function hasInvalidDateValue(value: string | null | undefined) {
  if (!value) return false;
  return !Number.isFinite(new Date(value).getTime());
}

function display(value: unknown) {
  if (value == null) return "-";
  const text = String(value).trim();
  return text || "-";
}

function highsenCandidateInfo(candidate: CandidateDepositRow): HighsenCandidateInfo | null {
  const raw = parseRawJsonObject(candidate.raw_json);
  const source = stringField(raw ?? undefined, "source");
  const externalId = candidate.external_id ?? "";
  const isHighsen =
    source === "highsen_gift_list_test" ||
    source === "highsen_gift_list" ||
    externalId.startsWith("highsen-test-gift-") ||
    externalId.startsWith("highsen-gift-");

  if (!isHighsen) return null;

  const statusLabel = candidateStatusLabel(candidate.status);
  return {
    sourceLabel: highsenSourceLabel(source, externalId),
    giftId: stringField(raw ?? undefined, "giftId") ?? highsenGiftIdFromExternalId(externalId),
    sender: stringField(raw ?? undefined, "sender") ?? "-",
    receiver: stringField(raw ?? undefined, "receiver") ?? candidate.member_account,
    giftAmount: numberField(raw ?? undefined, "giftAmount"),
    rate: numberField(raw ?? undefined, "rate"),
    adGroup: stringField(raw ?? undefined, "adGroup") ?? "-",
    rawStatus: stringField(raw ?? undefined, "status") ?? "-",
    importStatus: candidate.status === "pending" ? "候選充值，尚未確認" : statusLabel,
    pendingReason:
      candidate.manual_confirm_disabled_reason ||
      candidate.next_step_message ||
      (candidate.status === "pending" ? "candidate_only_import" : "-")
  };
}

function manualCandidateInfo(candidate: CandidateDepositRow): ManualCandidateInfo | null {
  const raw = parseRawJsonObject(candidate.raw_json);
  const source = stringField(raw ?? undefined, "source");
  if (source !== "manual_admin") return null;

  const statusLabel = candidateStatusLabel(candidate.status);
  return {
    sourceLabel: "後台手動新增",
    memberAccount: stringField(raw ?? undefined, "memberAccount") ?? candidate.member_account,
    amount: numberField(raw ?? undefined, "amount"),
    depositedAt: stringField(raw ?? undefined, "depositedAt") ?? candidate.deposited_at,
    channel: stringField(raw ?? undefined, "channel") ?? "-",
    externalId: stringField(raw ?? undefined, "externalId") ?? candidate.external_id ?? "-",
    note: stringField(raw ?? undefined, "note") ?? "-",
    evidenceNote: stringField(raw ?? undefined, "evidenceNote") ?? "-",
    importStatus: candidate.status === "pending" ? "候選充值，尚未確認" : statusLabel,
    pendingReason: stringField(raw ?? undefined, "pendingReason") ?? "manual_admin_pending_review"
  };
}

function parseRawJsonObject(value: string | null): JsonObject | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as JsonObject) : null;
  } catch {
    return null;
  }
}

function highsenSourceLabel(source: string | null, externalId: string) {
  if (source === "highsen_gift_list_test" || externalId.startsWith("highsen-test-gift-")) return "Highsen 贈禮列表（test888）";
  if (source === "highsen_gift_list" || externalId.startsWith("highsen-gift-")) return "Highsen 贈禮列表";
  return "Highsen 贈禮列表";
}

function highsenGiftIdFromExternalId(externalId: string) {
  if (!externalId) return "";
  return externalId.replace(/^highsen-test-gift-/, "").replace(/^highsen-gift-/, "");
}

function formatHighsenNumber(value: number | null) {
  return value == null ? "-" : new Intl.NumberFormat("zh-TW").format(value);
}

function formatRawJson(value: string | null) {
  if (!value) return "-";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function toMessage(error: unknown, fallback: string) {
  if (!(error instanceof Error) || !error.message) return fallback;
  return translateApiError(error.message) ?? error.message;
}

function translateApiError(message: string) {
  const normalized = message.trim().toLowerCase();
  const map: Record<string, string> = {
    "candidate already confirmed": "此候選充值已確認，不能重複操作。",
    "candidate not pending": "此候選充值已處理，不能重複操作。",
    "duplicate source + external_id": "已有相同來源交易的正式充值，不能重複確認。",
    "member not found": "找不到會員，不能確認為正式充值。",
    "reason is required": "請先填寫原因。",
    "missing external_id": "候選充值缺少外部交易 ID，不能確認。",
    "missing member_account": "候選充值缺少會員帳號，不能確認。",
    "invalid candidate id": "候選充值 ID 不正確。",
    "invalid amount": "候選充值金額不正確。",
    "invalid deposited_at": "候選充值時間不正確。",
    "candidate not found": "找不到候選充值資料。"
  };
  return map[normalized] ?? null;
}
